import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Avatar, Chip,
  List, ListItem, ListItemAvatar, ListItemText,
  CircularProgress, Button, Divider
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Refresh as RefreshIcon,
  MyLocation as MyLocationIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import ModernLayout from '../common/ModernLayout';
import { GradientButton } from '../common/ModernButton';
import axiosInstance from '../axiosConfig';

const EmployeeMap = () => {
  const [workingEmployees, setWorkingEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchWorkingEmployees();
  }, []);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(fetchWorkingEmployees, 30000); // Refresh every 30s
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const fetchWorkingEmployees = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/schedule/who-is-working');
      setWorkingEmployees(response.data.data || []);
    } catch (error) {
      console.error('Error fetching working employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityColor = (activityType) => {
    switch (activityType) {
      case 'working': return '#4CAF50';
      case 'break': return '#FF9800';
      case 'traveling': return '#2196F3';
      case 'at_office': return '#9C27B0';
      case 'remote': return '#00BCD4';
      default: return '#757575';
    }
  };

  const getActivityLabel = (activityType) => {
    switch (activityType) {
      case 'working': return 'Trabajando';
      case 'break': return 'En descanso';
      case 'traveling': return 'Viajando';
      case 'at_office': return 'En oficina';
      case 'remote': return 'Remoto';
      default: return 'Desconocido';
    }
  };

  const formatDuration = (hours) => {
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  return (
    <ModernLayout title="Mapa de Empleados">
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Empleados Trabajando Ahora
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {workingEmployees.length} empleados activos
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant={autoRefresh ? 'contained' : 'outlined'}
              onClick={() => setAutoRefresh(!autoRefresh)}
              size="small"
            >
              Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
            </Button>
            <GradientButton
              startIcon={<RefreshIcon />}
              onClick={fetchWorkingEmployees}
              disabled={loading}
            >
              Actualizar
            </GradientButton>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {/* Employee List */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Lista de Empleados
              </Typography>

              {loading && workingEmployees.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : workingEmployees.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No hay empleados trabajando actualmente
                  </Typography>
                </Box>
              ) : (
                <List>
                  {workingEmployees.map((employee, index) => (
                    <React.Fragment key={employee.id}>
                      <ListItem
                        button
                        onClick={() => setSelectedEmployee(employee)}
                        selected={selectedEmployee?.id === employee.id}
                        sx={{
                          borderRadius: 2,
                          mb: 1,
                          bgcolor: selectedEmployee?.id === employee.id ? 'action.selected' : 'transparent'
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: getActivityColor(employee.activity_type)
                            }}
                          >
                            {employee.employee_name[0]}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {employee.employee_name}
                              </Typography>
                              {employee.latitude && employee.longitude && (
                                <LocationIcon fontSize="small" color="primary" />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {employee.employee_role} • {employee.department}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                <Chip
                                  label={employee.shift_name}
                                  size="small"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                                {employee.activity_type && (
                                  <Chip
                                    label={getActivityLabel(employee.activity_type)}
                                    size="small"
                                    sx={{
                                      bgcolor: getActivityColor(employee.activity_type),
                                      color: 'white',
                                      fontSize: '0.7rem'
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                          }
                        />
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary">
                            <TimeIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                            {formatDuration(employee.hours_worked || 0)}
                          </Typography>
                        </Box>
                      </ListItem>
                      {index < workingEmployees.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          {/* Employee Details / Map Placeholder */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {selectedEmployee ? 'Detalles del Empleado' : 'Mapa de Ubicaciones'}
              </Typography>

              {selectedEmployee ? (
                <Box>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        margin: '0 auto',
                        mb: 2,
                        bgcolor: getActivityColor(selectedEmployee.activity_type),
                        fontSize: '2rem'
                      }}
                    >
                      {selectedEmployee.employee_name[0]}
                    </Avatar>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                      {selectedEmployee.employee_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {selectedEmployee.employee_role}
                    </Typography>
                    <Chip
                      label={getActivityLabel(selectedEmployee.activity_type)}
                      sx={{
                        bgcolor: getActivityColor(selectedEmployee.activity_type),
                        color: 'white'
                      }}
                    />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Turno Actual
                      </Typography>
                      <Typography variant="body1">
                        {selectedEmployee.shift_name}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Hora de Check-in
                      </Typography>
                      <Typography variant="body1">
                        {new Date(selectedEmployee.checked_in_at).toLocaleTimeString()}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Horas Trabajadas
                      </Typography>
                      <Typography variant="body1">
                        {formatDuration(selectedEmployee.hours_worked || 0)}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Departamento
                      </Typography>
                      <Typography variant="body1">
                        {selectedEmployee.department}
                      </Typography>
                    </Box>

                    {selectedEmployee.location_address && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Ubicación Actual
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.5 }}>
                          <LocationIcon color="primary" fontSize="small" />
                          <Typography variant="body1">
                            {selectedEmployee.location_address}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {selectedEmployee.latitude && selectedEmployee.longitude && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Coordenadas
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                          {selectedEmployee.latitude.toFixed(6)}, {selectedEmployee.longitude.toFixed(6)}
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<MyLocationIcon />}
                          onClick={() => {
                            window.open(
                              `https://www.google.com/maps?q=${selectedEmployee.latitude},${selectedEmployee.longitude}`,
                              '_blank'
                            );
                          }}
                          sx={{ mt: 1 }}
                        >
                          Ver en Google Maps
                        </Button>
                      </Box>
                    )}
                  </Box>

                  {/* Map Placeholder */}
                  {selectedEmployee.latitude && selectedEmployee.longitude && (
                    <Box
                      sx={{
                        mt: 3,
                        height: 300,
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: 1,
                        borderColor: 'divider'
                      }}
                    >
                      {/* 
                        Aquí puedes integrar un mapa real usando:
                        - Google Maps API
                        - Mapbox
                        - Leaflet
                        
                        Por ahora mostramos un iframe de Google Maps
                      */}
                      <iframe
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        style={{ border: 0 }}
                        src={`https://www.google.com/maps/embed/v1/place?key=YOUR_GOOGLE_MAPS_API_KEY&q=${selectedEmployee.latitude},${selectedEmployee.longitude}&zoom=15`}
                        allowFullScreen
                        title="Employee Location"
                      />
                    </Box>
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 400,
                    color: 'text.secondary'
                  }}
                >
                  <LocationIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Selecciona un empleado para ver su ubicación
                  </Typography>
                  <Typography variant="body2">
                    {workingEmployees.length} empleados con ubicación disponible
                  </Typography>

                  {/* Summary Stats */}
                  {workingEmployees.length > 0 && (
                    <Box sx={{ mt: 4, width: '100%' }}>
                      <Typography variant="subtitle2" sx={{ mb: 2 }}>
                        Resumen de Actividades
                      </Typography>
                      {['working', 'break', 'traveling', 'at_office', 'remote'].map(activity => {
                        const count = workingEmployees.filter(e => e.activity_type === activity).length;
                        if (count === 0) return null;
                        return (
                          <Box
                            key={activity}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              p: 1,
                              mb: 1,
                              borderRadius: 1,
                              bgcolor: 'background.default'
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  bgcolor: getActivityColor(activity)
                                }}
                              />
                              <Typography variant="body2">
                                {getActivityLabel(activity)}
                              </Typography>
                            </Box>
                            <Chip label={count} size="small" />
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </ModernLayout>
  );
};

export default EmployeeMap;

